import React, { useState, useEffect } from 'react';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import { supabase } from '../../utils/supabase';

interface AdminUser {
  id: string;
  email: string;
  created_at: string;
}

export function AdminUsers() {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadAdminUsers();
  }, []);

  const loadAdminUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setAdminUsers(data || []);
    } catch (error) {
      console.error('Error loading admin users:', error);
      setError('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!newEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('admin_users')
        .insert([{
          email: newEmail.trim().toLowerCase(),
          created_by: user.id
        }]);

      if (error) {
        if (error.code === '23505') {
          setError('This email is already an admin');
          return;
        }
        throw error;
      }

      setSuccess('Admin user added successfully');
      setNewEmail('');
      loadAdminUsers();
    } catch (error) {
      console.error('Error adding admin:', error);
      setError('Failed to add admin user');
    }
  };

  const handleRemoveAdmin = async (email: string) => {
    if (!confirm(`Are you sure you want to remove admin rights from ${email}?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('email', email);

      if (error) throw error;

      setSuccess('Admin user removed successfully');
      loadAdminUsers();
    } catch (error) {
      console.error('Error removing admin:', error);
      setError('Failed to remove admin user');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Manage Admin Users</h2>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-md">
            {success}
          </div>
        )}

        <form onSubmit={handleAddAdmin} className="mb-8">
          <div className="flex gap-4">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter email address"
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
            <button
              type="submit"
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Admin
            </button>
          </div>
        </form>

        <div className="space-y-4">
          {adminUsers.map((admin) => (
            <div
              key={admin.id}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div>
                <p className="font-medium text-gray-900">{admin.email}</p>
                <p className="text-sm text-gray-500">
                  Added {new Date(admin.created_at).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => handleRemoveAdmin(admin.email)}
                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          ))}

          {adminUsers.length === 0 && (
            <p className="text-center text-gray-500 py-4">
              No admin users found
            </p>
          )}
        </div>
      </div>
    </div>
  );
}